/**
 * AutoComplete Field Component
 * Wrapper for MUI Autocomplete with react-hook-form integration
 */
import React, { useMemo } from 'react'
import { Autocomplete, TextField, CircularProgress } from '@mui/material'
import { Controller } from 'react-hook-form'
import { FormFieldProps, FieldOption } from '../types'
import { useFormContext } from '../FormContext'
import { buildValidationRules } from '../utils/validationRules'

const AutoCompleteField: React.FC<FormFieldProps> = ({
  config,
  control,
  errors,
  readOnly
}) => {
  const {
    name,
    label,
    options = [],
    required,
    disabled,
    helperText,
    placeholder,
    dataSource,
    fieldProps = {}
  } = config

  // get the original values from the form context for change detection
  const formContext = useFormContext()
  const formValues = formContext?.values || {}

  // get the data source data
  const dataSources = formContext?.dataSources || {}
  const dataSourceState = dataSource ? dataSources[dataSource] : null

  // merge the options: use the options from the data source first, if there is no data source or the data source is not loaded, use the options from the config
  const mergedOptions = useMemo<FieldOption[]>(() => {
    if (
      dataSource &&
      dataSourceState?.options &&
      dataSourceState.options.length > 0
    ) {
      return dataSourceState.options
    }
    return options
  }, [dataSource, dataSourceState, options])

  // check if the data source is loading
  const isLoading = dataSource ? dataSourceState?.loading === true : false

  // 从fieldProps中提取MUI Autocomplete和TextField的属性
  const {
    multiple = false,
    getOptionLabel,
    InputLabelProps,
    InputProps: fieldInputProps,
    sx: fieldSx,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    allowClear: _allowClear,
    ...otherAutocompleteProps
  } = fieldProps

  // check if the field is changed
  const isFieldChanged = () => {
    // check the field level configuration, if the change highlighting is not enabled, return false
    if (config.enableChangeHighlight !== true) {
      return false
    }

    // if the change highlighting is enabled, check if the field is changed
    if (formContext) {
      const originalValues = formContext.originalValues || {}
      const currentValues = formValues

      // compare the original value and the current value
      const originalValue = originalValues[name]
      const currentValue = currentValues[name]

      if (multiple) {
        // For multiple selection, compare arrays
        const origArray = Array.isArray(originalValue) ? originalValue : []
        const currArray = Array.isArray(currentValue) ? currentValue : []

        // Compare array lengths and contents
        if (origArray.length !== currArray.length) return true
        return !origArray.every((val, index) => val === currArray[index])
      } else {
        // handle the single select value
        const origSelectValue = originalValue?.toString() || ''
        const currSelectValue = currentValue?.toString() || ''

        // only return true when the value is really different
        return origSelectValue !== currSelectValue
      }
    }
    return false
  }

  // check if the field is changed
  const isChanged = isFieldChanged()

  // 合并样式
  const mergedSx = {
    '& .MuiInputBase-root': {
      // For multiple selection, use auto height to accommodate chips
      // For single selection, use fixed height
      minHeight: '36px',
      ...(!multiple && { height: '36px' })
    },
    ...(isChanged && {
      '& .MuiInputBase-input': {
        color: 'secondary.dark',
        fontWeight: 'bold'
      }
    }),
    ...(fieldSx || {})
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={buildValidationRules(config)}
      render={({ field: { onChange, value, ref, ...field } }) => {
        // Find the selected option(s) from options array
        let selectedOption: FieldOption | FieldOption[] | null = null

        if (multiple) {
          // For multiple selection, value should be an array
          const valueArray = Array.isArray(value) ? value : []
          selectedOption = valueArray
            .map(val =>
              mergedOptions.find((option: FieldOption) => option.value === val)
            )
            .filter(Boolean) as FieldOption[]
        } else {
          // For single selection, value is a single value
          selectedOption =
            value !== null && value !== undefined
              ? mergedOptions.find(
                  (option: FieldOption) => option.value === value
                ) || null
              : null
        }

        // handle the data source error
        //
        // Defensive coercion: every branch must produce a string (or undefined).
        // The previous OR fallback `... || dataSourceState.error || ...` could
        // return the raw error object — typically the API client's
        // `{type, code, additional, message}` shape — which then flows into
        // MUI's `helperText` and crashes React with "Objects are not valid
        // as a React child" (the `additional` key trips the invariant).
        // That manifested specifically in jsdom when MSW returned an empty
        // partners list and the data source resolved with a falsy `.message`
        // / `.msg`. Real backend never hit it because the partners list is
        // non-empty there.
        const dsErr = dataSource && dataSourceState?.error
          ? dataSourceState.error
          : null
        const dsErrMessage = dsErr
          ? (typeof dsErr === 'string' ? dsErr : dsErr?.message || dsErr?.msg) ||
            'Failed to load options'
          : null

        const rhfErrMessage =
          typeof errors[name]?.message === 'string'
            ? (errors[name]?.message as string)
            : undefined

        const helperTextString =
          typeof helperText === 'string' ? helperText : undefined

        const errorMessage = rhfErrMessage || dsErrMessage || helperTextString

        // check if the error is displayed
        const hasError =
          Boolean(errors[name]) || Boolean(dataSource && dataSourceState?.error)

        return (
          <Autocomplete
            multiple={multiple}
            options={mergedOptions}
            value={selectedOption}
            disableCloseOnSelect={multiple}
            onChange={(_, newValue) => {
              if (multiple) {
                // For multiple selection, newValue is an array
                const values = Array.isArray(newValue)
                  ? newValue.map((option: FieldOption) => option.value)
                  : []
                onChange(values)
              } else {
                // For single selection, newValue is a single option or null
                const singleValue = newValue as FieldOption | null
                if (singleValue) {
                  onChange(singleValue.value)
                } else {
                  onChange('')
                }
              }
            }}
            getOptionLabel={option => {
              if (typeof getOptionLabel === 'function') {
                return getOptionLabel(option)
              }
              return option.label || ''
            }}
            disabled={disabled || readOnly === true || isLoading}
            loading={isLoading}
            renderInput={params => (
              <TextField
                {...params}
                {...field}
                label={label}
                placeholder={placeholder || fieldProps.placeholder}
                variant='outlined'
                size='small'
                required={required}
                error={hasError}
                helperText={errorMessage}
                inputRef={ref}
                InputProps={{
                  ...params.InputProps,
                  ...(fieldInputProps || {}),
                  endAdornment: (
                    <>
                      {isLoading ? (
                        <CircularProgress color='inherit' size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
                InputLabelProps={InputLabelProps}
                sx={mergedSx}
              />
            )}
            {...otherAutocompleteProps}
          />
        )
      }}
    />
  )
}

export default AutoCompleteField
